'use client';
import React, { useState } from 'react';
import { Loader2, CheckCircle } from 'lucide-react';
import { ParsedProfile, RateConfig } from './OnboardingWizard';
import BadgeTierChip from '@/components/ui/BadgeTierChip';
import { toast } from 'sonner';

interface Step4PreviewProps {
  expertId: string | null;
  profile: ParsedProfile;
  rates: RateConfig;
  onBack: () => void;
  onSubmit: () => void;
}

export default function Step4Preview({ expertId, profile, rates, onBack, onSubmit }: Step4PreviewProps) {
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!expertId) {
      toast.error('Profile not ready — refresh and try again');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch('/api/expert/submit-profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ expert_id: expertId, profile, rates }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Submit failed');
      }
      toast.success('Profile submitted for review');
      onSubmit();
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Submit failed';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const colors = ['#f0b429', '#2dd4bf', '#a78bfa', '#fb7185', '#34d399'];
  const colorIndex = profile.avatarSeed.charCodeAt(profile.avatarSeed.length - 1) % colors.length;
  const initial = profile.pseudonym.replace('Advisor #', '').charAt(0);

  return (
    <div className="flex flex-col gap-6">
      <div className="surface-elevated p-5">
        <h2 className="font-heading text-xl text-foreground mb-1">Preview Your Public Profile</h2>
        <p className="text-sm text-muted-foreground">
          This is exactly what buyers will see. Real name, employer, and institution are permanently hidden.
        </p>
      </div>

      <div className="surface-elevated p-6 flex flex-col gap-5">
        <div className="flex items-start gap-4">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center font-mono-data font-bold text-background text-2xl flex-shrink-0"
            style={{ background: colors[colorIndex] }}
            role="img"
            aria-label={`Avatar for ${profile.pseudonym}`}
          >
            {initial}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap mb-1">
              <span className="font-mono-data text-lg font-bold text-foreground">{profile.pseudonym}</span>
              <BadgeTierChip tier="NONE" size="sm" />
            </div>
            <p className="text-xs text-muted-foreground font-mono-data">{profile.experienceTier} · Pending Review</p>
          </div>
        </div>

        <p className="text-sm text-foreground leading-relaxed">{profile.headline}</p>

        <div className="flex flex-wrap gap-1.5">
          {profile.domainTags.map((tag) => (
            <span key={`preview-tag-${tag}`} className="text-xs font-mono-data px-2.5 py-1 rounded-full bg-primary/8 text-primary border border-primary/15">
              {tag}
            </span>
          ))}
        </div>

        <div className="border-t border-border pt-4">
          <p className="text-xs font-mono-data text-muted-foreground uppercase tracking-wider mb-2">Summary Bio</p>
          <p className="text-sm text-muted-foreground leading-relaxed">{profile.summaryBio}</p>
        </div>

        <div className="border-t border-border pt-4">
          <p className="text-xs font-mono-data text-muted-foreground uppercase tracking-wider mb-3">Key Strengths</p>
          <ul className="flex flex-col gap-2">
            {profile.keyStrengths.map((s, i) => (
              <li key={`preview-strength-${i}`} className="flex items-start gap-2.5 text-sm text-foreground">
                <span className="text-primary mt-0.5 flex-shrink-0">◆</span>
                {s}
              </li>
            ))}
          </ul>
        </div>

        <div className="border-t border-border pt-4">
          <p className="text-xs font-mono-data text-muted-foreground uppercase tracking-wider mb-2">Engagement Types</p>
          <div className="flex flex-wrap gap-1.5">
            {profile.engagementTypes.map((et) => (
              <span key={`preview-et-${et}`} className="text-xs font-mono-data px-2.5 py-1 rounded-full bg-secondary/10 text-secondary border border-secondary/20">
                {et}
              </span>
            ))}
          </div>
        </div>

        <div className="border-t border-border pt-4 grid grid-cols-3 gap-4">
          {[
            { label: 'Hourly', value: `₹${rates.hourlyRate.toLocaleString('en-IN')}` },
            { label: 'Session (2hr)', value: `₹${rates.sessionRate.toLocaleString('en-IN')}` },
            { label: 'Rush', value: `${rates.rushMultiplier}×` },
          ].map((item) => (
            <div key={`rate-preview-${item.label}`} className="text-center p-3 rounded-lg bg-muted/30 border border-border">
              <div className="font-mono-data text-sm font-semibold text-foreground">{item.value}</div>
              <div className="text-[10px] text-muted-foreground mt-0.5">{item.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="surface-elevated p-4 flex items-start gap-3">
        <CheckCircle size={16} className="text-secondary flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-foreground">Ready to submit</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Your profile will be submitted with verification_status = PENDING. An admin will review within 24–48 hours.
            You will not appear in buyer search until approved.
          </p>
        </div>
      </div>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          disabled={submitting}
          className="flex-1 py-3 px-6 rounded-lg border border-border text-muted-foreground font-medium text-sm hover:border-primary/30 hover:text-foreground active:scale-95 transition-all duration-150 disabled:opacity-40"
        >
          Back
        </button>
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="flex-1 py-3 px-6 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 active:scale-95 transition-all duration-150 disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {submitting ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Submitting…
            </>
          ) : (
            'Submit for Review'
          )}
        </button>
      </div>
    </div>
  );
}